import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  MenuItem,
  Typography,
  Button,
  alpha,
} from "@mui/material";
import { Icon } from "@iconify/react";
import personFill from "@iconify/icons-eva/person-fill";
import lockFill from "@iconify/icons-eva/lock-fill";
import { useSnackbar } from "notistack";
import MenuPopover from "../MenuPopover";
import { s3baseUrl } from "../../config/config";
import { logout } from "../../DAL/AUTH";
import ConfirmDialog from "./ConfirmDialog";
import { usePGIMode } from "../../Hooks/PGIModeContext";

export default function AccountPopover() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { profileNewImage, userName } = usePGIMode();
  const anchorRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [openLogout, setOpenLogout] = useState(false);

  const userEmail = localStorage.getItem("email");
  const userImage = profileNewImage
    ? profileNewImage.includes("blob:")
      ? profileNewImage
      : `${s3baseUrl}${profileNewImage}?t=${new Date().getTime()}`
    : null;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleProfile = () => {
    navigate("/profile");
    handleClose();
  };

  const handleChangePassword = () => {
    navigate("/change-password");
    handleClose();
  };
  const handle2FA = () => {
    navigate("/two-factor-authentication");
    handleClose();
  };

  const handleLogoutClick = () => {
    handleClose();
    setOpenLogout(true);
  };

  const handleConfirmLogout = async () => {
    const response = await logout();
    if (response.code === 200) {
      enqueueSnackbar(response.message, { variant: "success" });
      localStorage.clear();
      navigate("/login");
    } else {
      enqueueSnackbar(response.message, { variant: "error" });
    }
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          padding: 0,
          width: 44,
          height: 44,
          ...(open && {
            "&:before": {
              zIndex: 1,
              content: "''",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              position: "absolute",
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
            },
          }),
        }}
      >
        <Avatar src={userImage} alt={userName}>
          {userName.charAt(0)}
        </Avatar>
      </IconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{ width: 220 }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle1" noWrap>
            {userName}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
            {userEmail}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        <MenuItem
          onClick={handleProfile}
          sx={{ typography: "body2", py: 1, px: 2.5 }}
        >
          <Box
            component={Icon}
            icon={personFill}
            sx={{ mr: 2, width: 24, height: 24 }}
          />
          Edit Profile
        </MenuItem>

        <MenuItem
          onClick={handle2FA}
          sx={{ typography: "body2", py: 1, px: 2.5 }}
        >
          <Box
            component={Icon}
            icon={"eva:shield-outline"}
            sx={{ mr: 2, width: 24, height: 24 }}
          />
          Two Factor Auth
        </MenuItem>

        <MenuItem
          onClick={handleChangePassword}
          sx={{ typography: "body2", py: 1, px: 2.5 }}
        >
          <Box
            component={Icon}
            icon={lockFill}
            sx={{ mr: 2, width: 24, height: 24 }}
          />
          Change Password
        </MenuItem> 

        <Box sx={{ p: 2, pt: 1.5 }}>
          <Button
            fullWidth
            color="inherit"
            variant="outlined"
            onClick={handleLogoutClick}
          >
            Logout
          </Button>
        </Box>
      </MenuPopover>

      <ConfirmDialog
        open={openLogout}
        onClose={() => setOpenLogout(false)}
        onConfirm={handleConfirmLogout}
        title="Logout"
        message="Are you sure you want to logout?"
      />
    </>
  );
}
